import React, { useEffect, useState, useRef } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderStyles from "../../../styles/PublicPage/header";
import CustomSVG from "../../../helpers/ButtomSVG/CustomSVG";
import { Stack, Typography } from "@mui/material";
import { typo } from "../../../assets/fonts/font";
import { ICollection } from "../../../models/ICollection";
import { getCollections } from "../../../api/CollectionAPI";
import colorConfigs from "../../../configs/colorConfigs";

interface SubmenuHeaderProps {
  appBarHeight: string;
  isMobile: boolean;
  isTablet: boolean;
}

const SubmenuHeader: React.FC<SubmenuHeaderProps> = ({
  appBarHeight,
  isMobile,
  isTablet,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submenuItems, setSubmenuItems] = useState<ICollection[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const collections = await getCollections();
        collections.sort((a, b) => a.priority - b.priority);
        setSubmenuItems(collections);
      } catch (error) {
        console.error("Failed to fetch collections:", error);
      }
    };

    fetchCollections();
  }, []);

  const handleNavigation = (path: string) => () => {
    navigate(path);
  };

  const isActive = (path: string) => location.pathname === path;

  const scroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <AppBar position="static" sx={{ ...HeaderStyles.submenu, width: "100%" }}>
      <Toolbar sx={{ ...HeaderStyles.toolbar, width: "100%", overflow: "hidden" }}>
        {submenuItems.length > 4 && (
          <IconButton onClick={() => scroll("left")} sx={{ color: colorConfigs.secondary }}>
            <KeyboardArrowLeftIcon />
          </IconButton>
        )}
        <Stack
          ref={containerRef}
          direction="row"
          spacing={2}
          sx={{
            width: "90%", 
            height: "100%",
            margin: "0 auto",
            justifyContent: "center",
            alignItems: "center",
            overflowX: "auto",
            scrollBehavior: "smooth",
            touchAction: "pan-y", 
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {submenuItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                flexShrink: 0,
                justifyContent: "center",
                alignItems: "center",
                padding: "0.5rem",
                cursor: "pointer",
                position: "relative",
                touchAction: "pan-y",
              }}
            >
              <Button
                color="inherit"
                onClick={handleNavigation(`/collections/${item.slug}`)}
                sx={{
                  width: "fit-content",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  // position: "relative",
                  padding: "0.5rem 1rem",
                  ...HeaderStyles.button,
                  ...HeaderStyles.getActiveButtonStyle(isActive(`/collections/${item.slug}`)),
                }}
              >
                <Typography
                  sx={{ ...typo.title, color: colorConfigs.topbarCollectionText }}
                >
                  {item.name}
                </Typography>
                <Stack
                  sx={{
                    position: "absolute",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: "translateY(-50%)",
                  }}
                >
                  <CustomSVG
                    strokeColor={colorConfigs.mainbody}
                    strokeWidth={1}
                  />
                </Stack>
              </Button>
            </Box>
          ))}
        </Stack>
        {submenuItems.length > 4 && (
          <IconButton onClick={() => scroll("right")} sx={{ color: colorConfigs.secondary }}>
            <KeyboardArrowRightIcon />
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default SubmenuHeader;